'use client';
import React, { useEffect, useState, useRef, useCallback, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface UserProfile {
  id: string;
  full_name: string;
  email: string;
}

interface ConversationPreview {
  id: string;
  updated_at: string;
  other_user: UserProfile;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_deleted: boolean;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const AVATAR_COLORS = [
  'bg-teal-500', 'bg-emerald-500', 'bg-sky-500', 'bg-violet-500',
  'bg-rose-500', 'bg-amber-500', 'bg-indigo-500', 'bg-pink-500',
];

function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── Voice Message Hook ───────────────────────────────────────────────────────
function useVoiceMessaging() {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [playingMsgId, setPlayingMsgId] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const startRecording = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm' :'audio/ogg';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start(100);
      setIsRecording(true);
      return true;
    } catch {
      return false;
    }
  }, []);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        recorder.stream.getTracks().forEach((t) => t.stop());
        resolve(blob);
      };
      recorder.stop();
      setIsRecording(false);
    });
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string | null> => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      const res = await fetch('/api/rafeeq/stt', { method: 'POST', body: formData });
      if (!res.ok) return null;
      const data = await res.json();
      return data.text ?? null;
    } catch {
      return null;
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  const playMessageTTS = useCallback(async (msgId: string, text: string) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingMsgId === msgId) {
      setPlayingMsgId(null);
      return;
    }
    setPlayingMsgId(msgId);
    try {
      const res = await fetch('/api/ai/elevenlabs-tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        setPlayingMsgId(null);
        return;
      }
      const audioBuffer = await res.arrayBuffer();
      const blob = new Blob([audioBuffer], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPlayingMsgId(null);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setPlayingMsgId(null);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      await audio.play();
    } catch {
      setPlayingMsgId(null);
    }
  }, [playingMsgId]);

  return { isRecording, isTranscribing, playingMsgId, startRecording, stopRecording, transcribeAudio, playMessageTTS };
}

// ─── Main Component ───────────────────────────────────────────────────────────
function MessagesPageInner() {
  const { user } = useAuth();
  const supabase = createClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();

  const [conversations, setConversations] = useState<ConversationPreview[]>([]);
  const [activeConversation, setActiveConversation] = useState<ConversationPreview | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [communityUsers, setCommunityUsers] = useState<UserProfile[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [contextBanner, setContextBanner] = useState<{ title: string; type: string } | null>(null);
  const deepLinkHandledRef = useRef(false);

  // ─── Track conversation IDs the user participates in ────────────────────
  const userConvIdsRef = useRef<string[]>([]);

  const {
    isRecording,
    isTranscribing,
    playingMsgId,
    startRecording,
    stopRecording,
    transcribeAudio,
    playMessageTTS,
  } = useVoiceMessaging();

  // ─── Load conversations ──────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (!user) return;
    setLoadingConversations(true);
    try {
      const { data: participantRows } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id);

      if (!participantRows || participantRows.length === 0) {
        setConversations([]);
        setLoadingConversations(false);
        return;
      }

      const convIds = participantRows.map((r: any) => r.conversation_id);

      const { data: convRows } = await supabase
        .from('conversations')
        .select('id, updated_at')
        .in('id', convIds)
        .order('updated_at', { ascending: false });

      if (!convRows || convRows.length === 0) {
        setConversations([]);
        setLoadingConversations(false);
        return;
      }

      const previews: ConversationPreview[] = [];
      for (const conv of convRows) {
        const { data: parts } = await supabase
          .from('conversation_participants')
          .select('user_id, last_read_at, user_profiles(id, full_name, email)')
          .eq('conversation_id', conv.id);

        const other = parts?.find((p: any) => p.user_id !== user.id);
        if (!other) continue;

        const { data: lastMsg } = await supabase
          .from('private_messages')
          .select('content, created_at')
          .eq('conversation_id', conv.id)
          .eq('is_deleted', false)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        const myPart = parts?.find((p: any) => p.user_id === user.id);
        const { count: unread } = await supabase
          .from('private_messages')
          .select('id', { count: 'exact', head: true })
          .eq('conversation_id', conv.id)
          .neq('sender_id', user.id)
          .gt('created_at', myPart?.last_read_at ?? '1970-01-01');

        previews.push({
          id: conv.id,
          updated_at: conv.updated_at,
          other_user: (other as any).user_profiles as UserProfile,
          last_message: lastMsg?.content ?? '',
          last_message_time: lastMsg?.created_at ?? conv.updated_at,
          unread_count: unread ?? 0,
        });
      }

      setConversations(previews.length > 0 ? previews : []);
    } catch {
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, [user, supabase]);

  // ─── Load messages ───────────────────────────────────────────────────────
  const loadMessages = useCallback(async (convId: string) => {
    if (!user) return;
    setLoadingMessages(true);
    try {
      const { data } = await supabase
        .from('private_messages')
        .select('*')
        .eq('conversation_id', convId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (data && data.length > 0) {
        setMessages(data);
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, [user, supabase]);

  // ─── Load community users ────────────────────────────────────────────────
  const loadCommunityUsers = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .neq('id', user.id)
        .limit(20);
      if (data && data.length > 0) setCommunityUsers(data);
    } catch {
      // keep empty list on error
    }
  }, [user, supabase]);

  // ─── Start new conversation ──────────────────────────────────────────────
  const startConversation = useCallback(async (targetUser: UserProfile) => {
    setShowNewMessage(false);
    const existing = conversations.find((c) => c.other_user.id === targetUser.id);
    if (existing) {
      setActiveConversation(existing);
      return;
    }

    if (user) {
      try {
        const { data: conv } = await supabase
          .from('conversations')
          .insert({})
          .select()
          .single();

        if (conv) {
          await supabase.from('conversation_participants').insert([
            { conversation_id: conv.id, user_id: user.id },
            { conversation_id: conv.id, user_id: targetUser.id },
          ]);

          const newConv: ConversationPreview = {
            id: conv.id,
            updated_at: conv.created_at,
            other_user: targetUser,
            last_message: '',
            last_message_time: conv.created_at,
            unread_count: 0,
          };
          setConversations((prev) => [newConv, ...prev]);
          setActiveConversation(newConv);
          setMessages([]);
        }
      } catch {
        const mockConv: ConversationPreview = {
          id: `mock-${Date.now()}`,
          updated_at: new Date().toISOString(),
          other_user: targetUser,
          last_message: '',
          last_message_time: new Date().toISOString(),
          unread_count: 0,
        };
        setConversations((prev) => [mockConv, ...prev]);
        setActiveConversation(mockConv);
        setMessages([]);
      }
    } else {
      const mockConv: ConversationPreview = {
        id: `mock-${Date.now()}`,
        updated_at: new Date().toISOString(),
        other_user: targetUser,
        last_message: '',
        last_message_time: new Date().toISOString(),
        unread_count: 0,
      };
      setConversations((prev) => [mockConv, ...prev]);
      setActiveConversation(mockConv);
      setMessages([]);
    }
  }, [conversations, user, supabase]);

  useEffect(() => {
    loadConversations();
    loadCommunityUsers();
  }, [loadConversations, loadCommunityUsers]);

  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation.id);
    }
  }, [activeConversation, loadMessages]);

  // ─── Deep-link: auto-open conversation from Entrepreneur Hub ────────────
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    const paramUserId = searchParams.get('userId');
    const paramUserName = searchParams.get('userName');
    const paramContext = searchParams.get('context');
    const paramContextTitle = searchParams.get('contextTitle');
    const paramContextType = searchParams.get('contextType');

    if (!paramUserName && !paramUserId) return;
    if (!user) return;

    deepLinkHandledRef.current = true;

    // Build pre-filled message based on context
    const buildContextMessage = () => {
      if (paramContext === 'trade_lead' && paramContextTitle) {
        const typeLabel = paramContextType === 'partner' ? 'partnership opportunity'
          : paramContextType === 'invest' ? 'investment opportunity'
          : paramContextType === 'buy'? 'buying inquiry' :'trade lead';
        return `Hi! I saw your ${typeLabel} "${paramContextTitle}" on the Entrepreneur Hub and I'd like to discuss it further. Are you still available?`;
      }
      if (paramContext === 'service_inquiry' && paramContextTitle) {
        return `Hi! I'm interested in your service "${paramContextTitle}" listed on the Entrepreneur Hub. Could you share more details about availability and pricing?`;
      }
      return '';
    };

    const contextMsg = buildContextMessage();

    const resolveAndOpen = async () => {
      let targetUser: UserProfile | null = null;

      // Try to find user by ID first
      if (paramUserId) {
        const { data } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .eq('id', paramUserId)
          .single();
        if (data) targetUser = data as UserProfile;
      }

      // Fallback: find by name
      if (!targetUser && paramUserName) {
        const { data } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .ilike('full_name', paramUserName)
          .neq('id', user.id)
          .limit(1)
          .single();
        if (data) targetUser = data as UserProfile;
      }

      // If still not found, create a placeholder so the UI opens
      if (!targetUser && paramUserName) {
        targetUser = {
          id: paramUserId || `placeholder-${Date.now()}`,
          full_name: paramUserName,
          email: '',
        };
      }

      if (!targetUser) return;

      // Set context banner
      if (paramContextTitle) {
        setContextBanner({
          title: paramContextTitle,
          type: paramContext === 'trade_lead' ? 'Trade Lead' : 'Service Inquiry',
        });
      }

      await startConversation(targetUser);

      // Pre-fill message input
      if (contextMsg) {
        setNewMessage(contextMsg);
      }
    };

    resolveAndOpen();
  }, [user, searchParams, supabase, startConversation]);

  // ─── Realtime: live message sync for active conversation ─────────────────
  useEffect(() => {
    if (!activeConversation || !user) return;

    const channel = supabase
      .channel(`messages:${activeConversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'private_messages',
          filter: `conversation_id=eq.${activeConversation.id}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          setMessages((prev) => {
            const alreadyExists = prev.some(
              (m) => m.id === newMsg.id || (m.id.startsWith('opt-') && m.sender_id === newMsg.sender_id && m.content === newMsg.content)
            );
            if (alreadyExists) {
              return prev.map((m) =>
                m.id.startsWith('opt-') && m.sender_id === newMsg.sender_id && m.content === newMsg.content
                  ? newMsg
                  : m
              );
            }
            return [...prev, newMsg];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeConversation?.id, user?.id]);

  // ─── Realtime: live conversation list updates ────────────────────────────
  useEffect(() => {
    if (!user) return;

    const setupConvSubscription = async () => {
      const { data: participantRows } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id);

      const convIds = participantRows?.map((r: any) => r.conversation_id) ?? [];
      userConvIdsRef.current = convIds;

      if (convIds.length === 0) return;

      const channel = supabase
        .channel(`conv-updates:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'private_messages',
          },
          (payload) => {
            const newMsg = payload.new as Message;
            if (!userConvIdsRef.current.includes(newMsg.conversation_id)) return;

            setConversations((prev) =>
              prev.map((conv) => {
                if (conv.id !== newMsg.conversation_id) return conv;
                const isUnreadFromOther = newMsg.sender_id !== user.id;
                return {
                  ...conv,
                  last_message: newMsg.content,
                  last_message_time: newMsg.created_at,
                  updated_at: newMsg.created_at,
                  unread_count: isUnreadFromOther
                    ? conv.unread_count + 1
                    : conv.unread_count,
                };
              })
            );
          }
        )
        .subscribe();

      return channel;
    };

    let channelRef: ReturnType<typeof supabase.channel> | undefined;
    setupConvSubscription().then((ch) => {
      channelRef = ch;
    });

    return () => {
      if (channelRef) supabase.removeChannel(channelRef);
    };
  }, [user?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Send message ────────────────────────────────────────────────────────
  const sendMessage = async (overrideContent?: string) => {
    const content = (overrideContent ?? newMessage).trim();
    if (!content || !activeConversation || sending) return;
    setSending(true);
    if (!overrideContent) setNewMessage('');

    const optimistic: Message = {
      id: `opt-${Date.now()}`,
      conversation_id: activeConversation.id,
      sender_id: user?.id ?? 'me',
      content,
      is_deleted: false,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);

    if (user) {
      try {
        await supabase.from('private_messages').insert({
          conversation_id: activeConversation.id,
          sender_id: user.id,
          content,
        });
        await loadMessages(activeConversation.id);
        await loadConversations();
      } catch {
        // keep optimistic
      }
    }
    setSending(false);
  };

  // ─── Voice record & send ─────────────────────────────────────────────────
  const handleVoiceButton = async () => {
    setVoiceError(null);
    if (isRecording) {
      const blob = await stopRecording();
      if (!blob) return;
      const text = await transcribeAudio(blob);
      if (text && text.trim()) {
        await sendMessage(`🎤 ${text.trim()}`);
      } else {
        setVoiceError('Could not transcribe audio. Please try again.');
      }
    } else {
      const ok = await startRecording();
      if (!ok) setVoiceError('Microphone access denied. Please allow microphone permissions.');
    }
  };

  const filteredConversations = conversations.filter((c) =>
    c.other_user.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = communityUsers.filter((u) =>
    u.full_name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  return (
    <AppLayout activeRoute="/messages">
      <div className="flex flex-col gap-0 h-[calc(100vh-120px)] lg:h-[calc(100vh-48px)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Icon name="ChatBubbleLeftRightIcon" size={22} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-700 text-foreground">Messages</h1>
              <p className="text-xs text-muted-foreground">Private conversations with your community</p>
            </div>
            {totalUnread > 0 && (
              <span className="bg-accent text-accent-foreground text-xs font-700 px-2 py-0.5 rounded-full">
                {totalUnread} new
              </span>
            )}
          </div>
          <button
            onClick={() => setShowNewMessage(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors duration-150"
          >
            <Icon name="PencilSquareIcon" size={16} />
            <span className="hidden sm:inline">New Message</span>
          </button>
        </div>

        {/* Main Layout */}
        <div className="flex flex-1 gap-4 overflow-hidden">
          {/* Conversation List */}
          <div className={`flex flex-col bg-card border border-border rounded-2xl overflow-hidden transition-all duration-200 ${activeConversation ? 'hidden lg:flex lg:w-80 xl:w-96 flex-shrink-0' : 'flex-1 lg:w-80 xl:w-96 lg:flex-none'}`}>
            {/* Search */}
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              {loadingConversations ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
                    <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-500 text-foreground">No conversations yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Start a conversation with someone from the community</p>
                </div>
              ) : (
                filteredConversations.map((conv) => {
                  const isActive = activeConversation?.id === conv.id;
                  return (
                    <button
                      key={conv.id}
                      onClick={() => setActiveConversation(conv)}
                      className={`w-full flex items-start gap-3 px-4 py-3.5 border-b border-border/50 transition-colors duration-150 text-left ${isActive ? 'bg-primary/8' : 'hover:bg-muted/60'}`}
                    >
                      <div className={`w-10 h-10 rounded-full ${avatarColor(conv.other_user.id)} flex items-center justify-center text-white text-sm font-600 flex-shrink-0`}>
                        {getInitials(conv.other_user.full_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-700 text-foreground' : 'font-500 text-foreground'}`}>
                            {conv.other_user.full_name}
                          </span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">{timeAgo(conv.last_message_time)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <p className={`text-xs truncate ${conv.unread_count > 0 ? 'text-foreground font-500' : 'text-muted-foreground'}`}>
                            {conv.last_message || 'Start a conversation'}
                          </p>
                          {conv.unread_count > 0 && (
                            <span className="bg-primary text-primary-foreground text-xs font-700 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Conversation View */}
          {activeConversation ? (
            <div className="flex-1 flex flex-col bg-card border border-border rounded-2xl overflow-hidden">
              {/* Chat Header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <button
                  onClick={() => { setActiveConversation(null); setContextBanner(null); }}
                  className="lg:hidden p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
                >
                  <Icon name="ChevronLeftIcon" size={18} />
                </button>
                <div className={`w-9 h-9 rounded-full ${avatarColor(activeConversation.other_user.id)} flex items-center justify-center text-white text-sm font-600 flex-shrink-0`}>
                  {getInitials(activeConversation.other_user.full_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-600 text-foreground truncate">{activeConversation.other_user.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{activeConversation.other_user.email}</p>
                </div>
                {/* Voice feature indicator */}
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 rounded-full">
                  <svg className="w-3 h-3 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 4a3 3 0 016 0v6a3 3 0 11-6 0V4zm-2 6a5 5 0 0010 0H13a3 3 0 01-6 0H5z" />
                  </svg>
                  <span className="text-xs text-primary font-500">Voice</span>
                </div>
              </div>

              {/* Context banner (from Entrepreneur Hub) */}
              {contextBanner && (
                <div className="mx-4 mt-3 px-3 py-2 bg-primary/8 border border-primary/20 rounded-xl flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name="RocketLaunchIcon" size={14} className="text-primary flex-shrink-0" />
                    <p className="text-xs text-primary font-500 truncate">
                      <span className="text-primary/70">{contextBanner.type}: </span>
                      {contextBanner.title}
                    </p>
                  </div>
                  <button onClick={() => setContextBanner(null)} className="text-primary/50 hover:text-primary flex-shrink-0">
                    <Icon name="XMarkIcon" size={14} />
                  </button>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-3">
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
                      <Icon name="ChatBubbleOvalLeftIcon" size={24} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm font-500 text-foreground">No messages yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Send a message or tap 🎤 to send a voice message</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.sender_id === (user?.id ?? 'me');
                    const isVoice = msg.content.startsWith('🎤 ');
                    const displayText = isVoice ? msg.content.slice(3) : msg.content;
                    const isPlaying = playingMsgId === msg.id;
                    return (
                      <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        {!isOwn && (
                          <div className={`w-7 h-7 rounded-full ${avatarColor(activeConversation.other_user.id)} flex items-center justify-center text-white text-xs font-600 flex-shrink-0 mr-2 mt-auto`}>
                            {getInitials(activeConversation.other_user.full_name)}
                          </div>
                        )}
                        <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                          <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed group relative ${isOwn ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm'}`}>
                            {isVoice && (
                              <span className={`inline-flex items-center gap-1 text-xs font-500 mb-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M7 4a3 3 0 016 0v6a3 3 0 11-6 0V4zm-2 6a5 5 0 0010 0H13a3 3 0 01-6 0H5z" />
                                </svg>
                                Voice message
                              </span>
                            )}
                            <div className="flex items-start gap-2">
                              <span className="flex-1">{displayText}</span>
                              <button
                                onClick={() => playMessageTTS(msg.id, displayText)}
                                title={isPlaying ? 'Stop' : 'Listen'}
                                className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-150 opacity-0 group-hover:opacity-100 ${
                                  isOwn
                                    ? 'bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground'
                                    : 'bg-foreground/10 hover:bg-foreground/20 text-foreground'
                                } ${isPlaying ? 'opacity-100' : ''}`}
                              >
                                {isPlaying ? (
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                ) : (
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground px-1">{formatTime(msg.created_at)}</span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Voice error banner */}
              {voiceError && (
                <div className="mx-4 mb-2 px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-xl flex items-center justify-between gap-2">
                  <p className="text-xs text-destructive">{voiceError}</p>
                  <button onClick={() => setVoiceError(null)} className="text-destructive/60 hover:text-destructive">
                    <Icon name="XMarkIcon" size={14} />
                  </button>
                </div>
              )}

              {/* Recording indicator */}
              {isRecording && (
                <div className="mx-4 mb-2 px-3 py-2 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                  <p className="text-xs text-rose-600 font-500">Recording… tap mic again to send</p>
                </div>
              )}

              {/* Transcribing indicator */}
              {isTranscribing && (
                <div className="mx-4 mb-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-xl flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs text-primary font-500">Transcribing voice message…</p>
                </div>
              )}

              {/* Input */}
              <div className="px-4 py-3 border-t border-border">
                <div className="flex items-end gap-2">
                  <button
                    onClick={handleVoiceButton}
                    disabled={isTranscribing || sending}
                    title={isRecording ? 'Stop recording & send' : 'Record voice message'}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isRecording
                        ? 'bg-rose-500 text-white animate-pulse' :'bg-muted border border-border text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/30'
                    }`}
                  >
                    {isTranscribing ? (
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M7 4a3 3 0 016 0v6a3 3 0 11-6 0V4zm-2 6a5 5 0 0010 0H13a3 3 0 01-6 0H5z" />
                      </svg>
                    )}
                  </button>

                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder={isRecording ? 'Recording… tap mic to stop' : 'Type a message or tap 🎤 to record…'}
                    rows={1}
                    disabled={isRecording || isTranscribing}
                    className="flex-1 px-4 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none max-h-32 disabled:opacity-60"
                    style={{ minHeight: '42px' }}
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!newMessage.trim() || sending || isRecording || isTranscribing}
                    className="w-10 h-10 bg-primary text-primary-foreground rounded-xl flex items-center justify-center hover:bg-primary/90 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    {sending ? (
                      <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Icon name="PaperAirplaneIcon" size={18} />
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5 px-1">
                  🎤 Tap mic to record · Hover a message to hear it read aloud
                </p>
              </div>
            </div>
          ) : (
            /* Empty state for desktop */
            <div className="hidden lg:flex flex-1 bg-card border border-border rounded-2xl items-center justify-center">
              <div className="flex flex-col items-center text-center px-8">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-primary" />
                </div>
                <h3 className="text-base font-600 text-foreground mb-1">Select a conversation</h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Choose a conversation from the list or start a new one to connect with your community
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 4a3 3 0 016 0v6a3 3 0 11-6 0V4zm-2 6a5 5 0 0010 0H13a3 3 0 01-6 0H5z" />
                  </svg>
                  <span>Voice messages powered by ElevenLabs</span>
                </div>
                <button
                  onClick={() => setShowNewMessage(true)}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors duration-150"
                >
                  <Icon name="PencilSquareIcon" size={16} />
                  New Message
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-base font-700 text-foreground">New Message</h2>
              <button
                onClick={() => setShowNewMessage(false)}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
            <div className="p-5">
              <div className="relative mb-4">
                <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search community members..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  autoFocus
                  className="w-full pl-9 pr-3 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto scrollbar-thin">
                {filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => startConversation(u)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted transition-colors duration-150 text-left"
                  >
                    <div className={`w-9 h-9 rounded-full ${avatarColor(u.id)} flex items-center justify-center text-white text-sm font-600 flex-shrink-0`}>
                      {getInitials(u.full_name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-500 text-foreground truncate">{u.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </button>
                ))}
                {filteredUsers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No members found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default function MessagesPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <MessagesPageInner />
    </Suspense>
  );
}
